// useKaraokeTranslation.js

import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  LIVESTREAM_KARAOKE_NAME_DISPLAY_ORDER,
  LIVESTREAM_LOVENSE_NAME_DISPLAY_ORDER,
} from '../RemoteConfigKeys.js';

import fetchTranslationByKey from '../action/fetchTranslationByKey.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

const getGiftI18nKey = id => `giftname_${id.replace(/-/g, '_').toLowerCase()}`;
const getProductI18nKey = id =>
  `${id.replace(/[^a-z\d]/gi, '_').toLowerCase()}`;

const useKaraokeTranslation = ({ productId, title, isToy }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const productI18nKey = productId ? getProductI18nKey(productId) : '';
  const giftI18nKey = productId ? getGiftI18nKey(productId) : '';
  const isProductTranslationFetching = useSelector(state =>
    getNetworkingData(
      state,
      ['translation', productI18nKey, i18n.language],
      'isFetching'
    )
  );

  const isProductTranslationFetched = useSelector(state =>
    getNetworkingData(
      state,
      ['translation', productI18nKey, i18n.language],
      'isFetched'
    )
  );
  const isGiftTranslationFetching = useSelector(state =>
    getNetworkingData(
      state,
      ['translation', giftI18nKey, i18n.language],
      'isFetching'
    )
  );
  const isGiftTranslationFetched = useSelector(state =>
    getNetworkingData(
      state,
      ['translation', giftI18nKey, i18n.language],
      'isFetched'
    )
  );
  const displayOrder = useSelector(state =>
    getRemoteConfigData(
      state,
      isToy
        ? LIVESTREAM_LOVENSE_NAME_DISPLAY_ORDER
        : LIVESTREAM_KARAOKE_NAME_DISPLAY_ORDER
    )
  );
  const displayTitle = useMemo(() => {
    return displayOrder
      .map(order => {
        switch (order) {
          case 'translation': {
            const hasProductTranslation = i18n.customExists(productI18nKey, {
              ns: TranslationNamespace.PRODUCT,
            });
            const hasGiftTranslation = i18n.customExists(giftI18nKey, {
              ns: TranslationNamespace.GIFT,
            });
            return !isProductTranslationFetching && hasProductTranslation
              ? t(productI18nKey, { ns: TranslationNamespace.PRODUCT })
              : !isGiftTranslationFetching && hasGiftTranslation
                ? t(giftI18nKey, { ns: TranslationNamespace.GIFT })
                : '';
          }
          case 'title': {
            return title;
          }
          case 'key':
          default: {
            return productI18nKey;
          }
        }
      })
      .reduce((result, current) => result || current, '');
  }, [
    displayOrder,
    giftI18nKey,
    i18n,
    isGiftTranslationFetching,
    isProductTranslationFetching,
    productI18nKey,
    t,
    title,
  ]);
  useEffect(() => {
    if (productId) {
      const productI18nKey = productId ? getProductI18nKey(productId) : '';
      const giftI18nKey = productId ? getGiftI18nKey(productId) : '';

      if (
        !i18n.customExists(productI18nKey, {
          ns: TranslationNamespace.PRODUCT,
        }) &&
        !i18n.customExists(giftI18nKey, { ns: TranslationNamespace.GIFT })
      ) {
        if (!isProductTranslationFetched)
          dispatch(
            fetchTranslationByKey({
              key: productI18nKey,
            })
          );
        if (!isGiftTranslationFetched)
          dispatch(
            fetchTranslationByKey({
              key: giftI18nKey,
            })
          );
      }
    }
  }, [
    i18n,
    productId,
    dispatch,
    isProductTranslationFetched,
    isGiftTranslationFetched,
  ]);

  return displayTitle;
};

export default useKaraokeTranslation;
